import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { XIcon } from "../../../../../components/Icons/Icons";
import type { PIMProduct } from "../../../../../types/types.PIM";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--compare-cols, 2), minmax(0, 1fr));
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background: ${({ theme }) => theme.resolve("color_neutral_white")};
  padding-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

/** Inherits visual language from CardProduct (CardWrapper tokens). */
const ProductCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.resolve("color_cardProduct_bg")};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border-radius: ${({ theme }) => theme.resolve("radius_cardProduct")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_cardProduct_border")};
  box-shadow: ${({ theme }) => theme.resolve("effect_cardProduct_shadow_x")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_y")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_blur")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_spread")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_color")};
  transition: box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme }) =>
      theme.resolve("color_cardProduct_border_hover")};
  }
`;

const ProductName = styled.div`
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_base")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  padding-right: ${({ theme }) => theme.resolve("spacing_xl")};
  word-break: break-word;
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${({ theme }) => theme.resolve("spacing_xxs")};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;

  &:hover {
    opacity: 0.7;
  }
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

type CompareProductHeaderProps = {
  products: PIMProduct[];
  getProductHref: (product: PIMProduct) => string;
  onRemove: (productNumber: string) => void;
  canRemove: boolean;
};

export function CompareProductHeader({
  products,
  getProductHref,
  onRemove,
  canRemove,
}: CompareProductHeaderProps) {
  const { t } = useTranslation();

  return (
    <StickyHeader style={{ ["--compare-cols" as string]: products.length }}>
      <HeaderRow>
        {products.map((product) => {
          const productNumber = product.product_number || product.id;
          return (
            <ProductCard key={product.id}>
              <CardLink to={getProductHref(product)}>
                <ProductName>{product.name || productNumber}</ProductName>
              </CardLink>
              {canRemove && (
                <RemoveButton
                  type="button"
                  aria-label={t("Remove {{name}} from comparison", {
                    name: product.name,
                  })}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(productNumber);
                  }}
                >
                  <XIcon width={16} height={16} />
                </RemoveButton>
              )}
            </ProductCard>
          );
        })}
      </HeaderRow>
    </StickyHeader>
  );
}
